// @file autolinker to transform text into text with links inside anchor tags
import type { AutolinkerConfig } from 'autolinker'
import { captureMessage } from 'bits/error_tracker'

const EMAIL_REGEX =
  /^[-a-z0-9~!$%^&*_=+}{\'?]+(\.[-a-z0-9~!$%^&*_=+}{\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i
const URL_REGEX = /^(https?:\/\/(?:www\.|(?!www))[^\s\.]+\.[^\s]{2,}|www\.[^\s]+\.[^\s]{2,})$/gi
const TEL_REGEX = /^(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?$/g

const DEFAULT_OPTS = {
  // Open links in new window
  newWindow: true,
  // Don't remove "https://www." etc from link text
  stripPrefix: false,
  // Don't remove trailing slash
  stripTrailingSlash: false,
  urls: {
    // Avoid autolinking filenames
    tldMatches: false,
  },
}

export interface LinkMatch {
  type: 'email' | 'tel' | 'url'
  href: string
}

export function match(text: string): LinkMatch | null {
  if (text.match(EMAIL_REGEX) != null) {
    return { type: 'email', href: `mailto:${text}` }
  }
  const matches = text.match(TEL_REGEX)
  if (matches != null) {
    const num = matches[0].replace(/[^a-zA-z0-9\+]/g, '')
    return { type: 'tel', href: `tel:${num}` }
  }
  if (text.match(URL_REGEX) != null) {
    return { type: 'url', href: text }
  }
  return null
}

export async function link(text: string, opts?: AutolinkerConfig | undefined): Promise<string> {
  try {
    const Autolinker = (await import('./autolinkerjs')).default
    if (Autolinker != null) {
      return Autolinker.link(text, Object.assign({}, DEFAULT_OPTS, opts ?? {}))
    } else {
      captureMessage('Autolinker not loaded', { level: 'warning', context: { url: window.location.href } })
      return text
    }
  } catch (error) {
    captureMessage('Error linking text', { level: 'error', context: { url: window.location.href } })
    return text
  }
}

// santize html tags before linking => only the link tags will work when passed to v-html
export async function sanitizedLink(text: string, opts?: AutolinkerConfig | undefined): Promise<string> {
  const sanitizeText = text
    .replace(/&/g, '&amp;') // Must happen first or else it will escape other just-escaped characters.
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')

  return await link(sanitizeText, opts)
}
